/**
 * Connector Catalog Service
 *
 * Builds and caches a searchable index of Azure-managed Logic Apps connectors
 * for the assistant's DiscoverConnectors tool. The index is populated lazily:
 *   1. `getConnectorIndex` — one paginated `/managedApis` call returns the full
 *      connector list (name, displayName, description). Cached for the session.
 *   2. `ensureConnectorOperations` — one `/managedApis/{name}/apiOperations`
 *      call per candidate connector, only when the matcher needs operation-
 *      level data. Results are cached on the connector entry.
 */

import {
  fetchAllAzureConnectors,
  fetchOperationsForAzureConnector,
  type Connector,
  type DiscoveryOperation,
} from './operationsService';
import { getLogicEndpoint } from './endpoints';

export interface ConnectorInfo {
  /** Internal connector name (e.g., 'teams', 'kusto', 'office365') */
  name: string;
  /** Display name (e.g., 'Microsoft Teams', 'Azure Data Explorer') */
  displayName: string;
  /** Description of what the connector does */
  description: string;
  /** Sample operation IDs and their descriptions (filled in lazily) */
  operations: Array<{
    operationId: string;
    displayName: string;
    description: string;
  }>;
  /** Keywords extracted from name, displayName, description, operations */
  searchKeywords: string[];
}

export interface ConnectorSearchResult {
  connector: ConnectorInfo;
  /** Relevance score (higher = better match) */
  score: number;
  /** Which search terms matched */
  matchedTerms: string[];
}

interface IndexCacheEntry {
  index: ConnectorInfo[] | null;
  inFlight: Promise<ConnectorInfo[]> | null;
}

// Indices are keyed by `{logicEndpoint, projectName}` so navigating to a
// different region/project in the same session doesn't reuse a stale catalog.
const indexCache = new Map<string, IndexCacheEntry>();

/** Build the cache key for the current request scope. */
function scopeKey(projectName: string): string {
  return `${getLogicEndpoint()}::${projectName}`;
}

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'with', 'this', 'that', 'from', 'your', 'you', 'are',
  'can', 'will', 'use', 'using',
]);

/**
 * Add tokens from `text` to `keywords`, dropping non-alphanumeric chars,
 * short tokens, and stop words.
 */
function addKeywords(
  keywords: Set<string>,
  text: string,
  minLength: number,
  splitter: RegExp = /\s+/,
): void {
  text.toLowerCase().split(splitter).forEach((word) => {
    const cleaned = word.replace(/[^a-z0-9]/g, '');
    if (cleaned.length > minLength && !STOP_WORDS.has(cleaned)) {
      keywords.add(cleaned);
    }
  });
}

/**
 * Build the connector index from a single paginated `/managedApis` request.
 * Returns name/displayName/description for every managed connector. Operations
 * are loaded on demand by `ensureConnectorOperations`.
 */
async function buildConnectorIndex(
  authToken: string,
  projectName: string,
): Promise<ConnectorInfo[]> {
  let connectors: Connector[] = [];
  try {
    connectors = await fetchAllAzureConnectors({ projectName, accessToken: authToken });
  } catch (error) {
    console.warn('[ConnectorCatalog] Failed to fetch managed connectors:', error);
    return [];
  }

  const indexed: ConnectorInfo[] = connectors.map((connector) => {
    const name = connector.name;
    const displayName = connector.properties.displayName ?? name;
    const description = connector.properties.description ?? '';

    const keywords = new Set<string>();
    addKeywords(keywords, name, 2, /[-_]/);
    addKeywords(keywords, displayName, 2);
    addKeywords(keywords, description, 3);

    return {
      name,
      displayName,
      description,
      operations: [],
      searchKeywords: Array.from(keywords),
    };
  });

  console.log(`[ConnectorCatalog] Indexed ${indexed.length} connectors`);
  return indexed;
}

/**
 * Get the connector index for the current `{logicEndpoint, projectName}`
 * scope, building (and caching) it if needed. Concurrent callers share the
 * same in-flight build promise. Switching to a different project or region
 * uses a separate cache slot, so stale data is never silently reused.
 */
export async function getConnectorIndex(
  authToken: string,
  projectName: string,
): Promise<ConnectorInfo[]> {
  const key = scopeKey(projectName);
  const entry = indexCache.get(key) ?? { index: null, inFlight: null };

  if (entry.index) return entry.index;
  if (entry.inFlight) return entry.inFlight;

  entry.inFlight = buildConnectorIndex(authToken, projectName);
  indexCache.set(key, entry);
  try {
    entry.index = await entry.inFlight;
    return entry.index;
  } finally {
    entry.inFlight = null;
  }
}

// Per-connector operation fetch dedupe map, keyed by `{scope, connectorName}`.
const operationFetches = new Map<string, Promise<ConnectorInfo['operations']>>();

/**
 * Lazily fetch and cache a connector's operation list. Mutates the connector
 * entry in place so subsequent `searchConnectors` calls benefit from the
 * operation-level scoring. Concurrent callers for the same connector share
 * a single in-flight request.
 *
 * Loaded on demand (rather than up front for ~1000 connectors) because the
 * full op-set per connector is large and the assistant only ever needs ops
 * for the handful of connectors a capability matches.
 */
export async function ensureConnectorOperations(
  connector: ConnectorInfo,
  authToken: string,
  projectName: string,
): Promise<ConnectorInfo['operations']> {
  if (connector.operations.length > 0) return connector.operations;

  const cacheKey = `${scopeKey(projectName)}::${connector.name}`;
  const inFlight = operationFetches.get(cacheKey);
  if (inFlight) return inFlight;

  const fetchPromise = (async () => {
    let ops: DiscoveryOperation[] = [];
    try {
      ops = await fetchOperationsForAzureConnector(
        { projectName, accessToken: authToken },
        connector.name,
        undefined,
        'assistant-catalog',
      );
    } catch (error) {
      console.warn(`[ConnectorCatalog] Failed to fetch operations for ${connector.name}:`, error);
      return connector.operations;
    }

    const mapped = ops
      .map((op) => ({
        operationId: op.name?.split('/').pop() ?? op.name ?? '',
        displayName: op.properties.summary || op.properties.description || op.name || '',
        description: op.properties.description ?? '',
      }))
      .filter((o) => o.operationId);

    connector.operations = mapped;

    // Fold operation display names into the connector's keyword set so the
    // next `searchConnectors` pass can match on op-level terms.
    const keywords = new Set<string>(connector.searchKeywords);
    mapped.forEach((op) => addKeywords(keywords, op.displayName, 3));
    connector.searchKeywords = Array.from(keywords);

    return mapped;
  })();

  operationFetches.set(cacheKey, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    operationFetches.delete(cacheKey);
  }
}

// First-party Microsoft/Azure connectors ranked by likely customer usage.
// Higher boost = more likely to be used by customers. Applied only as a
// tiebreaker when the connector already matched search terms (score > 0).
// Tiers are checked in order: specific prefixes in Tier 1 match before the
// generic "azure" catch-all in Tier 3 (e.g. "azureblob" → Tier 1, not Tier 3).
// Prefixes also catch user-specific forks (e.g. "kusto-anladwig", "azuread-gijeyaku").
const PREFERRED_CONNECTOR_TIERS: ReadonlyArray<{
  prefixes: string[];
  exact: ReadonlySet<string>;
  boost: number;
}> = [
  {
    // Tier 1 — Core Azure & Microsoft services used by most customers
    boost: 100,
    prefixes: [
      'azureblob',                   // Azure Blob Storage
      'azureopenai',                 // Azure OpenAI
      'azuread',                     // Azure AD / Entra ID (azuread, azureadapplications, azureadip)
      'office365',                   // Office 365 (office365, office365groups, office365groupsmail, office365users, office365video)
      'teams',                       // Microsoft Teams (teams, teamsspirit, teamsvirtualevents)
      'sharepointonline',            // SharePoint Online
      'onedrive',                    // OneDrive (onedrive, onedriveforbusiness)
      'excelonline',                 // Excel Online (excelonline, excelonlinebusiness)
      'outlook',                     // Outlook (outlook, outlooktasks)
    ],
    exact: new Set([
      'github',                      // GitHub
      'sql',                         // Azure SQL
      'servicebus',                  // Azure Service Bus
      'keyvault',                    // Azure Key Vault
      'commondataservice',           // Dataverse / Common Data Service
      'eventhubs',                   // Azure Event Hubs
      'documentdb',                  // Azure Cosmos DB
      'kusto',                       // Azure Data Explorer
    ]),
  },
  {
    // Tier 2 — Popular Azure AI, enterprise, and productivity services
    boost: 50,
    prefixes: [
      'azureagentservice',           // Azure AI Foundry Agent Service
      'azureaifoundry',              // Azure AI Foundry Inference
      'azureaisearch',               // Azure AI Search (azureaisearch, azureaisearchfoundryiq)
      'formrecognizer',              // Azure AI Document Intelligence
      'dynamics',                    // Dynamics 365 (dynamicsax, dynamicscrmonline, dynamicsnavision, etc.)
      'powerbi',                     // Power BI
      'powerplatform',               // Power Platform Admin (powerplatformadminv2, powerplatformforadmins)
      'linkedin',                    // LinkedIn (linkedin, linkedinv2)
      'azuremysql',                  // Azure Database for MySQL
      'azurequeues',                 // Azure Queues
      'azuretables',                 // Azure Tables
      'azurefile',                   // Azure File Storage
      'acs',                         // Azure Communication Services (acschat, acsemail, acsidentity, acssmsevents)
      'cognitiveservices',           // Azure Cognitive Services (vision, text, QnA, etc.)
      'visualstudioteamservices',    // Azure DevOps
      'a365',                        // Agent 365 MCP servers (a365adminmcp, a365copilotchatmcp, etc.)
    ],
    exact: new Set([
      'sqldw',                       // Azure SQL Data Warehouse
      'planner',                     // Microsoft Planner
      'onenote',                     // Microsoft OneNote
      'todo',                        // Microsoft To Do
      'salesforce',                  // Salesforce
      'slack',                       // Slack
      'sendgrid',                    // SendGrid
      'twilio',                      // Twilio
      'openaiassistants',            // OpenAI Assistants
    ]),
  },
  {
    // Tier 3 — Other Azure/Microsoft services, popular third-party, catch-all
    boost: 20,
    prefixes: [
      'azureappservice',             // Azure App Service
      'azureautomation',             // Azure Automation
      'azuredatafactory',            // Azure Data Factory
      'azuredatalake',               // Azure Data Lake
      'azuredigitaltwins',           // Azure Digital Twins
      'azureeventgrid',              // Azure Event Grid (azureeventgrid, azureeventgridpublish)
      'azureiotcentral',             // Azure IoT Central
      'azureloganalytics',           // Azure Log Analytics (azureloganalytics, azureloganalyticsdatacollector)
      'azuremonitorlogs',            // Azure Monitor Logs
      'azurevm',                     // Azure Virtual Machines
      'azuresentinel',               // Microsoft Sentinel
      'fhir',                        // Microsoft FHIR (fhirbase, fhirclinical, fhirlink)
      'microsoft',                   // Generic Microsoft prefix (microsoftforms, microsoftbookings, etc.)
      'google',                      // Google services (googledrive, googlesheet, googlecalendar, etc.)
      'copilotfor',                  // Copilot for Sales/Service
      'sap',                         // SAP (sap, sapodata)
      'azure',                       // Catch-all for any other Azure connector
    ],
    exact: new Set([
      'jira',                        // Jira
      'trello',                      // Trello
      'asana',                       // Asana
      'zendesk',                     // Zendesk
      'dropbox',                     // Dropbox
      'bitbucket',                   // Bitbucket
      'smtp',                        // SMTP
      'sftp',                        // SFTP
      'sftpwithssh',                 // SFTP with SSH
      'ftp',                         // FTP
      'rss',                         // RSS
      'twitter',                     // Twitter
      'wordonlinebusiness',          // Word Online
      'crossbeam',                   // Copilot for Sales
      'webcontents',                 // Web Contents
      'webcontentsv2',               // Web Contents v2
      'powervirtualagents',          // Power Virtual Agents
      'googlegemini',                // Google Gemini
    ]),
  },
];

function getPreferredBoost(connectorName: string): number {
  const lower = connectorName.toLowerCase();
  for (const tier of PREFERRED_CONNECTOR_TIERS) {
    if (tier.exact.has(lower)) return tier.boost;
    if (tier.prefixes.some(p => lower.startsWith(p))) return tier.boost;
  }
  return 0;
}

/**
 * Search the connector index for capabilities
 */
export function searchConnectors(
  index: ConnectorInfo[],
  capability: string,
  maxResults: number = 5
): ConnectorSearchResult[] {
  const searchTerms = capability.toLowerCase()
    .split(/\s+/)
    .map(term => term.replace(/[^a-z0-9]/g, ''))
    .filter(term => term.length > 2);

  const results: ConnectorSearchResult[] = [];

  for (const connector of index) {
    let score = 0;
    const matchedTerms: string[] = [];

    for (const term of searchTerms) {
      // Exact name match (highest priority)
      if (connector.name.toLowerCase() === term) {
        score += 100;
        matchedTerms.push(`name:${term}`);
      } else if (connector.name.toLowerCase().includes(term)) {
        score += 50;
        matchedTerms.push(`name:${term}`);
      }

      // Display name match
      if (connector.displayName.toLowerCase().includes(term)) {
        score += 30;
        matchedTerms.push(`displayName:${term}`);
      }

      // Keyword match
      if (connector.searchKeywords.some(kw => kw.includes(term) || term.includes(kw))) {
        score += 20;
        matchedTerms.push(`keyword:${term}`);
      }

      // Operation match
      for (const op of connector.operations) {
        if (op.displayName.toLowerCase().includes(term) || op.operationId.toLowerCase().includes(term)) {
          score += 15;
          matchedTerms.push(`operation:${term}`);
          break; // Only count once per term
        }
      }
    }

    if (score > 0) {
      // Boost first-party Microsoft / Azure connectors
      const boost = getPreferredBoost(connector.name);
      if (boost > 0) {
        score += boost;
        matchedTerms.push(`boost:${boost}`);
      }

      results.push({ connector, score, matchedTerms });
    }
  }

  // Sort by score descending
  results.sort((a, b) => b.score - a.score);

  return results.slice(0, maxResults);
}

/**
 * Find the best matching connector for a capability description
 */
export function findBestConnector(
  index: ConnectorInfo[],
  capabilityDescription: string
): ConnectorSearchResult | null {
  const results = searchConnectors(index, capabilityDescription, 1);
  return results.length > 0 ? results[0] ?? null : null;
}

/**
 * Clear cached indices and in-flight fetches.
 *
 * - With no args: drops every scope (e.g. on sign-out or full reset).
 * - With `projectName`: drops only the entry for the current `getLogicEndpoint()`
 *   scope and any of its connector-operation caches.
 */
export function clearConnectorIndex(projectName?: string): void {
  if (!projectName) {
    indexCache.clear();
    operationFetches.clear();
    console.log('[ConnectorCatalog] All indices cleared');
    return;
  }

  const key = scopeKey(projectName);
  indexCache.delete(key);
  const opPrefix = `${key}::`;
  for (const opKey of operationFetches.keys()) {
    if (opKey.startsWith(opPrefix)) operationFetches.delete(opKey);
  }
  console.log(`[ConnectorCatalog] Index cleared for ${key}`);
}

/**
 * Whether an index is already cached for the current scope (or, if no
 * `projectName` is supplied, any scope).
 */
export function isIndexLoaded(projectName?: string): boolean {
  if (projectName) {
    return !!indexCache.get(scopeKey(projectName))?.index;
  }
  for (const entry of indexCache.values()) {
    if (entry.index) return true;
  }
  return false;
}

/**
 * Aggregate stats across every cached scope (used for diagnostic logging).
 */
export function getIndexStats(): { loaded: boolean; connectorCount: number; operationCount: number } {
  let connectorCount = 0;
  let operationCount = 0;
  let loaded = false;
  for (const entry of indexCache.values()) {
    if (!entry.index) continue;
    loaded = true;
    connectorCount += entry.index.length;
    operationCount += entry.index.reduce((sum, c) => sum + c.operations.length, 0);
  }
  return { loaded, connectorCount, operationCount };
}
